import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

import { LeftCard, RightCard } from '@sandtech-hub-monorepo/ui-kit';
import { useCognito } from '@sandtech-hub-monorepo/ui-auth-kit';

import {
  confirmSignUpTitle,
  signUpCTAQuestion,
  signUpCTAText,
} from '../../../../stringsManager';
import { ConfirmSignUpForm } from './components/ConfirmSignUpForm';
import { isAlreadyConfirmed, linkErrorMessage } from '../linkError';
import {
  RefereeBottomCard,
  RefereeLeftCard,
} from '../SignUp/components/RefereeLeftCard';

export interface ConfirmSignUpProps {
  loginRoute: string;
  confirmSignUpCover: string;
  afterAutoSignIn: string;
  /**
   * Fired after a successful email signup confirmation (and the subsequent
   * autoSignIn, if the flow includes it). Social signup flows do not reach
   * this component — they go through CognitoProvider's onSocialSignIn.
   */
  onSignUpComplete?: () => void;
  /**
   * Optional cover chrome: a logo drawn over the cover image, anchored top
   * (default) or bottom, plus a dark gradient scrim for photos that don't
   * have enough natural contrast for a light logo.
   */
  /**
   * Renders the ALX referral marketing panels when the URL carries a `ref`
   * code. Defaults to true; pass false on brands that have no referral
   * programme, so the normal cover is used instead. Does not affect the
   * `custom:referredBy` attribute, which is written either way.
   */
  showRefereeMarketing?: boolean;
  /** The confirmation email links here with the code, so the page has no code input. */
  codeByLink?: boolean;
  coverLogo?: React.ReactNode;
  coverLogoPosition?: 'top' | 'bottom';
  overlay?: boolean;
  overlayClassName?: string;
}

// SignUp double-encodes the email; a stray '%' must not crash the page.
const safeDecode = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const ConfirmSignUp = (props: ConfirmSignUpProps) => {
  const navigate = useNavigate();

  const { loading, confirmSignUp, autoSignIn } = useCognito();
  const [params] = useSearchParams();
  const referralCode = params.get('ref')?.trim();
  const linkEmail = safeDecode(params.get('email') || '');
  const linkCode = params.get('code') ?? '';
  // Opened from the email link: everything needed is in the URL, so confirm
  // straight away instead of asking for a click.
  const confirmFromLink = !!props.codeByLink && !!linkEmail && !!linkCode;
  const [linkError, setLinkError] = useState<string | null>(null);
  const confirmStarted = useRef(false);

  // The link's one-time code has no business on the login page.
  const loginParams = new URLSearchParams(params);
  loginParams.delete('code');
  const loginSearch = loginParams.toString();
  // The link opens a new tab, so there is no session to sign into: log in.
  const goToLogin = () =>
    navigate({ pathname: props.loginRoute, search: loginSearch });

  const handleConfirmSignUp = async (
    values: {
      email: string;
      confirmationCode: string;
    },
    onError?: (error: unknown) => void
  ) => {
    const confirmSignUpResult = await confirmSignUp(
      {
        username: values.email,
        confirmationCode: values.confirmationCode,
      },
      onError
    );

    if (confirmSignUpResult && confirmSignUpResult.isSignUpComplete) {
      if (confirmSignUpResult.nextStep.signUpStep === 'COMPLETE_AUTO_SIGN_IN') {
        // Call `autoSignIn` API to complete the flow
        const autoSignInResult = await autoSignIn();
        if (autoSignInResult) {
          if (
            autoSignInResult.nextStep.signInStep === 'DONE' ||
            autoSignInResult.isSignedIn
          ) {
            props.onSignUpComplete?.();
            toast.success('You have successfully signed up. Welcome!');
            navigate({ pathname: props.afterAutoSignIn });
          }
        }
      } else {
        props.onSignUpComplete?.();
        toast.success(
          'You have successfully signed up. Login to access your account!'
        );
        goToLogin();
      }
    }
  };

  useEffect(() => {
    if (!confirmFromLink || confirmStarted.current) return;
    confirmStarted.current = true;
    handleConfirmSignUp(
      { email: linkEmail, confirmationCode: linkCode },
      (error) => {
        if (isAlreadyConfirmed(error)) {
          toast.success('Your email is already confirmed. Log in to continue.');
          goToLogin();
          return;
        }
        setLinkError(
          linkErrorMessage(error, 'Use Resend Link below to get a new one.')
        );
      }
    );
  }, []);

  const showRefereeMarketing =
    (props.showRefereeMarketing ?? true) && !!referralCode;

  return (
    <>
      {showRefereeMarketing ? (
        <RefereeLeftCard />
      ) : (
        <LeftCard
          backgroundImage={props.confirmSignUpCover}
          logo={props.coverLogo}
          logoPosition={props.coverLogoPosition}
          overlay={props.overlay}
          overlayClassName={props.overlayClassName}
        />
      )}
      <RightCard>
        <h1 className="text-2xl font-semibold tracking-tight">
          {confirmSignUpTitle}
        </h1>

        {confirmFromLink && !linkError ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="animate-spin" />
            Confirming your email…
          </div>
        ) : (
          <ConfirmSignUpForm
            loading={loading}
            onSubmit={(values) => handleConfirmSignUp(values)}
            codeByLink={props.codeByLink}
            linkError={linkError ?? undefined}
            defaultValues={{
              email: linkEmail,
              // A failed link shows the resend form rather than the dead code.
              confirmationCode: linkError ? '' : linkCode,
            }}
          />
        )}
        <div className="flex gap-1 justify-center leading-tight">
          <p className="text-sm text-muted-foreground">{signUpCTAQuestion}</p>
          <Link
            to={`${props.loginRoute}${loginSearch ? '?' + loginSearch : ''}`}
            className="text-sm text-primary leading-tight"
          >
            {signUpCTAText}
          </Link>
        </div>
      </RightCard>
      {showRefereeMarketing && <RefereeBottomCard />}
    </>
  );
};
